import { ArchiveAudioRecorder } from './ArchiveAudioRecorder'
import type { TeamsAudioFrame } from '@/services/teams-gateway-audio'

/** The Teams bot negotiates `AudioFormat.Pcm16K`, so every gateway frame is 16 kHz mono PCM16. */
const GATEWAY_SAMPLE_RATE = 16_000

/**
 * How far ahead of "now" the recording timeline is anchored.
 *
 * A frame can only be placed at the moment it was spoken if that moment is still
 * in the future when it reaches this browser, and it never is: the frame has to
 * cross the gateway, the socket and the JS event loop first. Anchoring the
 * timeline flush against `currentTime` therefore left every frame late, and a
 * late frame is clamped to "play now" — so playback order became arrival order.
 * Frames delivered in a burst then stacked on top of each other (a speaker
 * comb-filtering against themselves) and frames delivered late tore holes at the
 * seams. This cushion absorbs that jitter so each frame lands where it belongs.
 *
 * Wide enough to cover socket coalescing and a GC pause, short enough that the
 * cushion is not itself a noticeable stretch of the recording.
 */
const SCHEDULE_LOOKAHEAD_SECONDS = 0.4

interface ArchiveBaseline {
  /** Gateway timestamp of the first frame recorded, the origin of the timeline. */
  timestampMs: number
  /** `AudioContext.currentTime` when that frame was scheduled. */
  contextTime: number
}

interface ArchiveSession {
  context: AudioContext
  /** Every frame connects here, not to the destination — see `createMixBus`. */
  mixBus: AudioNode
  destination: MediaStreamAudioDestinationNode
  recorder: ArchiveAudioRecorder
}

export interface NativeMeetingAudioArchiveOptions {
  createAudioContext?: () => AudioContext
  createRecorder?: () => ArchiveAudioRecorder
}

/**
 * Decodes one little-endian PCM16 gateway frame into the Float32 samples WebAudio takes.
 *
 * A trailing odd byte is dropped rather than read past the frame — the gateway
 * always sends whole samples, but a truncated frame must not corrupt the buffer.
 */
export function pcm16ToFloat32(bytes: Uint8Array): Float32Array {
  const sampleCount = Math.floor(bytes.byteLength / 2)
  const samples = new Float32Array(sampleCount)
  const view = new DataView(bytes.buffer, bytes.byteOffset, sampleCount * 2)
  for (let index = 0; index < sampleCount; index += 1) {
    samples[index] = view.getInt16(index * 2, true) / 0x8000
  }
  return samples
}

export interface FrameSchedule {
  /** The `AudioBufferSourceNode.start` time, in context seconds. */
  startTime: number
  /** Timeline origin the NEXT frame should be measured against. */
  baseline: ArchiveBaseline
}

/**
 * Opens a recording timeline whose origin sits far enough ahead of now that
 * arriving frames can still be placed at the moment they were spoken.
 *
 * @param frameTimestampMs Gateway timestamp of the frame opening the timeline.
 * @param currentTime `AudioContext.currentTime` at scheduling time.
 */
export function anchorTimeline(frameTimestampMs: number, currentTime: number): ArchiveBaseline {
  return { timestampMs: frameTimestampMs, contextTime: currentTime + SCHEDULE_LOOKAHEAD_SECONDS }
}

/**
 * Places a frame on the recording timeline at the moment it was actually spoken.
 *
 * Teams only sends audio for whoever is speaking, so a participant's frames come
 * with gaps in them. Scheduling by gateway timestamp leaves those gaps as real
 * silence; concatenating frames instead would shrink the recording and drift it
 * out of sync with the transcript's own timestamps.
 *
 * A frame whose slot has already passed re-opens the timeline instead of being
 * clamped to "play now". Clamping is what made the recording noisy: once the
 * cushion is gone every later frame is late too, so they all pile onto whatever
 * `currentTime` happens to be and stack on each other. Re-anchoring buys the
 * cushion back, at the cost of a one-off stretch where the delay happened —
 * which only occurs when the media clock jumps or the tab stalls for longer
 * than the whole lookahead.
 *
 * @param frameTimestampMs Gateway timestamp of the frame being scheduled.
 * @param baseline Timeline origin in force for this frame.
 * @param currentTime `AudioContext.currentTime` at scheduling time.
 * @returns Where to start this frame, and the baseline to carry forward.
 */
export function resolveFrameStartTime(
  frameTimestampMs: number,
  baseline: ArchiveBaseline,
  currentTime: number
): FrameSchedule {
  const scheduled = baseline.contextTime + (frameTimestampMs - baseline.timestampMs) / 1000
  if (scheduled >= currentTime) return { startTime: scheduled, baseline }

  const reanchored = anchorTimeline(frameTimestampMs, currentTime)
  return { startTime: reanchored.contextTime, baseline: reanchored }
}

/**
 * Builds the node every frame is mixed through, so several people talking at
 * once cannot drive the sum past full scale.
 *
 * Each participant is scheduled as its own source, and WebAudio simply adds
 * them: three speakers peaking together clipped hard on the way into the Opus
 * encoder, which is heard as tearing on exactly the crosstalk the recording
 * exists to capture. A compressor with no knee and a high ratio acts as a
 * brick-wall limiter — inert while one person speaks (nothing reaches the
 * threshold) and catching only the overlaps, rather than a fixed attenuation
 * that would make every single-speaker recording quiet to protect against a
 * case that is usually not happening.
 *
 * Falls back to the destination itself on a context that cannot build one, so a
 * stub context (or an old browser) still records.
 */
function createMixBus(context: AudioContext, destination: MediaStreamAudioDestinationNode): AudioNode {
  if (typeof context.createDynamicsCompressor !== 'function') return destination

  const limiter = context.createDynamicsCompressor()
  limiter.threshold.value = -3
  limiter.knee.value = 0
  limiter.ratio.value = 20
  limiter.attack.value = 0.003
  limiter.release.value = 0.25
  limiter.connect(destination)
  return limiter
}

/**
 * A 16 kHz AudioContext when the browser allows it, falling back to the default
 * rate — `createBuffer` still declares 16 kHz per buffer, so WebAudio resamples
 * on playback and the recording stays correct either way.
 */
function createDefaultAudioContext(): AudioContext {
  try {
    return new AudioContext({ sampleRate: GATEWAY_SAMPLE_RATE })
  } catch {
    return new AudioContext()
  }
}

/**
 * Records a Teams Native meeting from the participant-tagged PCM the gateway
 * already streams to this browser, so the meeting can be played back later.
 *
 * Share Tab recordings hand a `MediaStream` straight to `ArchiveAudioRecorder`;
 * native mode has no such stream — the gateway sends raw PCM per participant —
 * which is why native meetings saved no audio at all. Each frame is scheduled
 * onto a `MediaStreamAudioDestinationNode` at its own timestamp, so overlapping
 * speakers mix and silence between turns is preserved, and the resulting stream
 * is encoded by the same `ArchiveAudioRecorder` (WebM/Opus) the Share Tab path
 * uploads to S3.
 */
export class NativeMeetingAudioArchive {
  private readonly createAudioContext: () => AudioContext
  private readonly createRecorder: () => ArchiveAudioRecorder
  private session: ArchiveSession | null = null
  private baseline: ArchiveBaseline | null = null

  constructor(options: NativeMeetingAudioArchiveOptions = {}) {
    this.createAudioContext = options.createAudioContext ?? createDefaultAudioContext
    this.createRecorder = options.createRecorder ?? (() => new ArchiveAudioRecorder())
  }

  /** No-ops when already recording, so a reconnecting gateway never restarts the file. */
  start(): void {
    if (this.session) return
    const context = this.createAudioContext()
    // Autoplay policy can hand back a suspended context; the meeting was started
    // by a click, so resuming is allowed here and a failure is not worth aborting on.
    void Promise.resolve(context.resume?.()).catch(() => {})
    const destination = context.createMediaStreamDestination()
    const mixBus = createMixBus(context, destination)
    const recorder = this.createRecorder()
    recorder.start(destination.stream)
    this.session = { context, mixBus, destination, recorder }
    this.baseline = null
  }

  write(frame: TeamsAudioFrame): void {
    const session = this.session
    if (!session) return

    const samples = pcm16ToFloat32(frame.pcm16)
    if (samples.length === 0) return

    const { context, mixBus } = session
    this.baseline ??= anchorTimeline(frame.timestampMs, context.currentTime)
    const schedule = resolveFrameStartTime(frame.timestampMs, this.baseline, context.currentTime)
    this.baseline = schedule.baseline

    const buffer = context.createBuffer(1, samples.length, GATEWAY_SAMPLE_RATE)
    buffer.getChannelData(0).set(samples)
    const source = context.createBufferSource()
    source.buffer = buffer
    source.connect(mixBus)
    source.start(schedule.startTime)
  }

  isRecording(): boolean {
    return this.session !== null
  }

  /**
   * Audio recorded since the previous call, for the periodic upload that keeps
   * a dead tab from taking the recording with it (BRAI-457).
   *
   * @returns The new audio, or null when this archive is not recording or
   *   nothing has been captured since the previous call.
   */
  takeUnsentChunks(): Blob | null {
    return this.session?.recorder.takeUnsentChunks() ?? null
  }

  /**
   * Finalizes the recording and releases the audio graph.
   *
   * The session is detached synchronously so a `start()` for the next meeting
   * cannot be swallowed while this one is still flushing its encoder.
   *
   * @returns The encoded recording, or `null` when nothing was captured.
   */
  async stop(): Promise<Blob | null> {
    const session = this.session
    this.session = null
    this.baseline = null
    if (!session) return null

    const blob = await session.recorder.stop()
    try {
      await session.context.close()
    } catch {
      /* already closed, or a stub context in tests */
    }
    return blob
  }
}
